/**
 * Landing page copy and structured content. Edit here to tune messaging without layout changes.
 */

export const problemVsSolutionContent = {
  heading: 'The Old Way vs The HUSTLi Way',
  subheading:
    'Youth coaching shouldn’t mean late nights in spreadsheets',
  oldWay: {
    label: 'The old way',
    items: [
      'Hours planning every practice by hand',
      'Recycling static templates that never quite fit',
      'Guessing drill order, timing, and structure',
      'Forgetting warmups, water breaks, or transitions',
      'Manual lineups and substitution tracking',
      'Human error in positioning and play time',
    ],
  },
  hustliWay: {
    label: 'The HUSTLi way',
    items: [
      'AI generated practice plans in seconds',
      'Guided drill sequencing that keeps practice moving',
      'Video links for drills when you want extra clarity',
      'Automated substitutions and lineup support',
      'More consistency, less guesswork on the field',
      'More time to actually coach your players',
    ],
  },
} as const;

export const featuresSectionHeading = 'Plan Faster. Coach Smarter.';

/** Subheading under the features section title */
export const featuresSectionSubheading =
  'AI that helps you plan faster, and coach better, on game day. Youth sports coaching moves fast. HUSTLi is built to save time, reduce stress, and keep your team organized from practice to game day.';

export type ShowcaseMediaType = 'image' | 'gif';

export type ShowcaseItem = {
  key: 'roster' | 'practice' | 'development' | 'game' | 'ai';
  title: string;
  description: string;
  mediaType: ShowcaseMediaType;
  /**
   * Drop assets into `public/assets/showcase/` and reference them like:
   * `/assets/showcase/roster.gif`
   */
  mediaSrc: string;
  mediaAlt: string;
};

export const featuresShowcaseItems: ShowcaseItem[] = [
  {
    key: 'roster',
    title: 'Roster management',
    description: 'Add players, track availability, and keep your team details organized.',
    mediaType: 'image',
    mediaSrc: '/assets/showcase/roster.png',
    mediaAlt: 'Roster management screen',
  },
  {
    key: 'practice',
    title: 'Practice planning',
    description: 'Build a full practice quickly and adjust drills as your week changes.',
    mediaType: 'gif',
    mediaSrc: '/assets/showcase/practice-planning.gif',
    mediaAlt: 'Practice planning flow animation',
  },
  {
    key: 'development',
    title: 'Player development',
    description: 'Track progress and notes so every athlete gets coaching attention.',
    mediaType: 'image',
    mediaSrc: '/assets/showcase/player-development.png',
    mediaAlt: 'Player development screen',
  },
  {
    key: 'game',
    title: 'Game planning',
    description: 'Prep lineups and game decisions so game day feels calmer and more consistent.',
    mediaType: 'image',
    mediaSrc: '/assets/showcase/game-planning.png',
    mediaAlt: 'Game planning screen',
  },
  {
    key: 'ai',
    title: 'AI Coach chat',
    description: 'Ask questions, get planning help, and move faster when time is tight.',
    mediaType: 'gif',
    mediaSrc: '/assets/showcase/ai-coach-chat.gif',
    mediaAlt: 'AI Coach chat animation',
  },
];

export type WhyChooseItem = {
  title: string;
  description: string;
};

/**
 * Three column group titles (explore block). Current defaults below.
 * Alternate title sets you can swap in:
 * Set A: Practice planning that fits real life | Fair play on game day | Team tools in one place
 * Set B: Built for real practices | Confidence on game day | Your team in one hub
 * Set C: Less time planning | Fair minutes and roles | Roster and game prep together
 */

export type FeatureTile = {
  title: string;
  description: string;
  icon: string;
};

export type FeatureGroup = {
  title: string;
  description: string;
  items: FeatureTile[];
};

export const featureGroups: FeatureGroup[] = [
  {
    title: 'AI Practice Planning',
    description: 'Spend minutes planning, not your whole evening.',
    items: [
      {
        title: 'AI practice plans',
        description:
          'Full practices generated fast, tuned to your sport with position specific drills.',
        icon: '🤖',
      },
      {
        title: 'Smart drill sequencing',
        description:
          'Logical flow from warmup to finish, with room to adjust on the fly.',
        icon: '🎯',
      },
      {
        title: 'Video drill support',
        description:
          'Drill video links when you want a quick visual. Great for new or volunteer coaches.',
        icon: '🎬',
      },
    ],
  },
  {
    title: 'Fair Play on Game Day',
    description: 'Fair play and clear roles, without mental gymnastics.',
    items: [
      {
        title: 'Substitution engine',
        description:
          'Automate subs and rotations so no one gets lost in the shuffle.',
        icon: '⚖️',
      },
      {
        title: 'Equal play support',
        description:
          'Track playing time and balance minutes. Less arguing, more transparency.',
        icon: '⏱️',
      },
      {
        title: 'Position and rotation logic',
        description:
          'Formations, positions, and rotation rules that stay consistent week to week.',
        icon: '🔄',
      },
    ],
  },
  {
    title: 'Advanced Team Tools',
    description: 'Roster, lineup, and game prep in one place.',
    items: [
      {
        title: 'Roster management',
        description:
          'Rosters, attendance, and injury notes. Player evaluations based on sport specific metrics.',
        icon: '👥',
      },
      {
        title: 'Lineup generation',
        description:
          'Depth charts and lineups with less back and forth. Go Pro for optimization based on player performance metrics.',
        icon: '📋',
      },
      {
        title: 'Game planning tools',
        description:
          'Game plans with play concept recommendations, substitutions, and strategy.',
          icon: '🏈',
      },
    ],
  },
];

export const trustBuilderContent = {
  heading: 'Explore what HUSTLi offers',
  subheading:
    'HUSTLi isn’t just faster than a template. It helps you avoid the small errors that cost big on game day. Dig into the tools that matter most for your season.',
  bullets: [
    'Fewer missed substitutions and rotation mixups',
    'Harder to forget a player when lineups and subs are structured',
    'Less lineup confusion between innings, quarters, or drives',
    'More even playing time, less manual tracking, fewer “did they play?” moments',
    'Clearer multi sport roster workflows with fewer handoff errors',
    'Rotation and lineup rules you set once and apply consistently',
  ],
  closingLine: 'Just fair, organized, confident coaching every time.',
} as const;

export const testimonialsSectionHeading = 'Stress-Free Coaches';

export const testimonialsSectionIntro =
  'Saving time, cutting stress, and feeling more prepared is essential for busy volunteer coaches.';

export type Testimonial = {
  quote: string;
  name: string;
  role: string;
};

export const testimonials: Testimonial[] = [
  {
    quote:
      'I get hours back every week. AI practice plans are a solid starting point. I tweak in seconds instead of building from zero.',
    name: 'John S.',
    role: 'Youth Soccer Coach',
  },
  {
    quote:
      'Lineup and depth chart tools cut our stress. Makes it easy to get our players in different positions quickly and easily. GameChanger import on Pro lets us keep track of their stats all in one place.',
    name: 'Sarah J.',
    role: 'Youth Baseball Coach',
  },
  {
    quote:
      'Coach Steve actually helps with lineup questions, formations, and creating plans from chat.',
    name: 'Mike W.',
    role: 'Football Coach',
  },
];

export const finalCtaContent = {
  heading: 'Stop Planning. Start Coaching.',
  body:
    '',
  buttonLabel: 'Sign Up Now',
} as const;
